import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText } from '../common';
import { FiX, FiZap, FiMessageSquare, FiGrid, FiUsers } from 'react-icons/fi';
import routes from '../../utils/routes';

interface UpgradeModalProps {
  isVisible: boolean;
  onClose: () => void;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({ isVisible, onClose }) => {
  const colors = useAppSelector((state) => state.theme.theme);
  const navigate = useNavigate();

  if (!isVisible) return null;

  const features = [
    {
      icon: FiMessageSquare,
      title: 'Unlimited AI Queries',
      description: 'Ask Copilot anything, anytime',
    },
    {
      icon: FiGrid,
      title: 'All 15+ Buckets',
      description: 'Access every curated portfolio',
    },
    {
      icon: FiUsers,
      title: '10% Expert Discount',
      description: 'Save on advisor consultations',
    },
    {
      icon: FiZap,
      title: 'Deep Search',
      description: 'Analyze full annual reports',
    },
  ];

  const handleUpgrade = () => {
    onClose();
    navigate(routes.pricing || '/pricing');
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[100] p-4 animate-fade-in"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={onClose}
    >
      <div
        className="rounded-2xl w-full max-w-md overflow-hidden animate-scale-in"
        style={{
          backgroundColor: colors.cardBackground || colors.backgroundColor,
          boxShadow: colors.shadowLarge,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div
          className="p-6 text-center relative"
          style={{
            background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
          }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/20 transition-colors"
          >
            {React.createElement(FiX as any, { size: 20, color: '#fff' })}
          </button>

          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3">
            {React.createElement(FiZap as any, { size: 32, color: '#fff' })}
          </div>

          <CText type="B20" color="#fff" style={{ fontWeight: 700 }}>
            Upgrade to Pro
          </CText>
          <CText type="r14" color="rgba(255,255,255,0.9)" style={{ marginTop: 4 }}>
            Unlock the full power of Yantrai
          </CText>
        </div>

        {/* Features */}
        <div className="p-6">
          <div className="space-y-4 mb-6">
            {features.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: colors.primary + '15' }}
                >
                  {React.createElement(feature.icon as any, { size: 20, color: colors.primary })}
                </div>
                <div>
                  <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>
                    {feature.title}
                  </CText>
                  <CText type="r12" color={colors.grayScale500}>
                    {feature.description}
                  </CText>
                </div>
              </div>
            ))}
          </div>

          {/* Pricing */}
          <div
            className="p-4 rounded-xl mb-4 text-center"
            style={{
              backgroundColor: colors.backgroundColor,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            <CText type="r12" color={colors.grayScale500}>
              Starting at just
            </CText>
            <div className="flex items-baseline justify-center gap-1 mt-1">
              <CText type="B24" color={colors.textColor} style={{ fontWeight: 700 }}>
                Rs.499
              </CText>
              <CText type="r14" color={colors.grayScale500}>
                /month
              </CText>
            </div>
            <CText type="r12" color={colors.primary} style={{ marginTop: 4 }}>
              7-day free trial included
            </CText>
          </div>

          {/* CTA Buttons */}
          <button
            onClick={handleUpgrade}
            className="w-full py-3.5 rounded-xl font-semibold text-base transition-all duration-200 hover:-translate-y-0.5 active:scale-98"
            style={{
              background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
              color: '#fff',
              boxShadow: colors.shadowMedium,
            }}
          >
            Start Free Trial
          </button>

          <button
            onClick={onClose}
            className="w-full py-3 mt-2 text-sm font-medium"
            style={{ color: colors.grayScale500 }}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
